import {
    storeReferalCode,
    retreieveReferalCode,
    removeReferalCode,
    storeCommissionRefCode,
    retrieveCommissionRefCode,
} from '../redux/authentication';
import { setBuzzBridgeSignupContext } from './buzzbridgeAppContext';

/** Signup / BuzzBridge loyalty referral query keys. */
export const SIGNUP_REFERRAL_QUERY_KEYS = ['referral_code', 'referral', 'affiliate_code', 'affiliate'] as const;

/** Explicit commission attribution query key (also `ref` on contest pages — see below). */
export const COMMISSION_QUERY_KEYS = ['commission_code'] as const;

function firstQueryValue(params: URLSearchParams, keys: readonly string[]): string | null {
    for (const key of keys) {
        const raw = params.get(key);
        if (!raw) continue;
        const code = raw.trim().split('?')[0].trim();
        if (code) return code;
    }
    return null;
}

function normalizePath(pathname: string): string {
    return (pathname || '').split('?')[0].toLowerCase();
}

/**
 * Contest public pages use /campaign/{slug}?ref=CODE for commission attribution.
 * Signup surfaces use /signup?ref=… / buzzbridge for loyalty referral.
 */
export function isContestCommissionPath(pathname: string): boolean {
    const path = normalizePath(pathname);
    if (!path) return false;
    if (path.includes('/buzzbridge')) return false;
    if (path.startsWith('/signup') || path.includes('/signup')) return false;
    if (path.startsWith('/login') || path.includes('/login')) return false;
    // Public contest view (and legacy /contest/)
    if (path.startsWith('/campaign/') || path === '/campaign') return true;
    if (path.startsWith('/contest/') || path === '/contest') return true;
    if (path.startsWith('/event/') || path === '/event') return true;
    return false;
}

export function isSignupReferralPath(pathname: string): boolean {
    const path = normalizePath(pathname);
    if (!path) return false;
    if (path.includes('/buzzbridge')) return true;
    if (path.startsWith('/signup') || path.includes('/signup')) return true;
    if (path.startsWith('/register') || path.includes('/register')) return true;
    return false;
}

export function extractCodesFromSearch(
    search: string,
    pathname = '',
): { signupReferral: string | null; commissionCode: string | null } {
    if (!search && !pathname) {
        return { signupReferral: null, commissionCode: null };
    }
    const params = new URLSearchParams(search.startsWith('?') ? search : search ? `?${search}` : '');
    const explicitCommission = firstQueryValue(params, COMMISSION_QUERY_KEYS);
    const explicitSignup = firstQueryValue(params, SIGNUP_REFERRAL_QUERY_KEYS);
    const genericRef = firstQueryValue(params, ['ref']);

    let commissionCode = explicitCommission;
    let signupReferral = explicitSignup;

    // Ambiguous `?ref=` — classify by page context
    if (genericRef) {
        if (isContestCommissionPath(pathname)) {
            commissionCode = commissionCode || genericRef;
        } else if (isSignupReferralPath(pathname)) {
            signupReferral = signupReferral || genericRef;
        } else {
            // Unknown surface: keep as signup referral (legacy BuzzBridge / shared links)
            signupReferral = signupReferral || genericRef;
        }
    }

    return { signupReferral, commissionCode };
}

export function extractCodesFromUrl(urlOrPath?: string): {
    signupReferral: string | null;
    commissionCode: string | null;
    pathname: string;
} {
    if (typeof window === 'undefined' && !urlOrPath) {
        return { signupReferral: null, commissionCode: null, pathname: '' };
    }
    try {
        const href = urlOrPath ?? window.location.href;
        const url = href.startsWith('http')
            ? new URL(href)
            : new URL(href, typeof window !== 'undefined' ? window.location.origin : 'https://thspian.com');
        const codes = extractCodesFromSearch(url.search, url.pathname);
        return { ...codes, pathname: url.pathname };
    } catch {
        const search = typeof window !== 'undefined' ? window.location.search : '';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        return { ...extractCodesFromSearch(search, pathname), pathname };
    }
}

/** @deprecated Prefer extractCodesFromSearch — kept for call sites that only need a single code. */
export function extractReferralCodeFromSearch(search: string): string | null {
    const { signupReferral, commissionCode } = extractCodesFromSearch(search, '');
    return signupReferral || commissionCode;
}

export function extractReferralCodeFromUrl(urlOrPath?: string): string | null {
    const { signupReferral, commissionCode } = extractCodesFromUrl(urlOrPath);
    return signupReferral || commissionCode;
}

/**
 * Persist attribution from the current URL.
 * - Contest pages + ?ref= / ?commission_code= → commission storage (checkout)
 * - Signup / BuzzBridge + ?ref= / ?referral_code= → signup referral storage
 * Returns whichever codes were stored.
 */
export function captureReferralCodeFromUrl(urlOrPath?: string): string | null {
    const { signupReferral, commissionCode, pathname } = extractCodesFromUrl(urlOrPath);

    if (commissionCode) {
        storeCommissionRefCode(commissionCode);
        // Older builds stored contest ?ref= under `referral`. If that value is this
        // commission code, clear it so signup does not treat a promo code as a referrer.
        void Promise.resolve(retreieveReferalCode()).then(existing => {
            if (typeof existing === 'string' && existing.trim().toUpperCase() === commissionCode.toUpperCase()) {
                removeReferalCode();
            }
        });
    }
    if (signupReferral) {
        storeReferalCode(signupReferral);
    }

    try {
        const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
        if (path.toLowerCase().includes('/buzzbridge') && (signupReferral || commissionCode)) {
            setBuzzBridgeSignupContext(true);
        }
    } catch {
        // ignore
    }

    return commissionCode || signupReferral;
}

/** Capture from window.location — safe to call at app boot before React Router. */
export function captureReferralCodeFromWindow(): string | null {
    if (typeof window === 'undefined') return null;
    return captureReferralCodeFromUrl(window.location.href);
}

/** Signup / BuzzBridge loyalty referrer code only. */
export async function getStoredReferralCode(): Promise<string | null> {
    try {
        const value = await Promise.resolve(retreieveReferalCode());
        if (typeof value !== 'string') return null;
        const code = value.trim().split('?')[0].trim();
        return code || null;
    } catch {
        return null;
    }
}

/** Contest commission promo code for submission checkout. */
export async function getStoredCommissionCode(): Promise<string | null> {
    try {
        const value = await Promise.resolve(retrieveCommissionRefCode());
        if (typeof value !== 'string') return null;
        const code = value.trim().split('?')[0].trim();
        return code || null;
    } catch {
        return null;
    }
}
